import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

async function loadLogoDataUrl() {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/hos-logo.webp";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } catch (err) {
    console.warn("Could not load logo for PDF", err);
    return null;
  }
}

export async function generateReportPDF(report, template) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const marginX = 40;
  let y = 50;

  const logo = await loadLogoDataUrl();
  if (logo) {
    try { doc.addImage(logo, "PNG", marginX, y - 30, 60, 60); } catch (e) { /* ignore */ }
  }

  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(20, 20, 20);
  doc.text("HOUSE OF SPELLS — TIMES SQUARE", marginX + (logo ? 72 : 0), y);
  y += 22;

  doc.setFontSize(13);
  doc.setTextColor(120, 90, 20);
  doc.text(`${report.type === "opening" ? "STORE OPENING" : "STORE CLOSING"} REPORT`, marginX, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const meta = [
    [`Report #`, report.report_number],
    [`Date`, report.business_date],
    [`Manager`, report.manager_name],
    [`Employee ID`, report.manager_employee_id || "—"],
    [`Start Time`, fmt(report.start_time)],
    [`Completed`, fmt(report.completed_time)],
    [`Actual ${report.type === "opening" ? "Open" : "Close"} Time`, fmt(report.actual_time)],
    [`Completion`, `${report.completion_pct || 0}%`],
    [`Status`, (report.status || "").toUpperCase()],
  ];
  meta.forEach(([k, v]) => {
    doc.text(`${k}:`, marginX, y);
    doc.text(String(v || "—"), marginX + 130, y);
    y += 14;
  });

  y += 6;
  autoTable(doc, {
    startY: y,
    head: [["#", "Task", "Section", "Status", "Completed At", "By", "Notes"]],
    body: (template?.items || []).map((it, idx) => {
      const r = (report.responses || []).find((x) => x.item_id === it.id) || {};
      return [
        idx + 1,
        it.title,
        it.section || "",
        r.completed ? "Done" : r.unable_reason ? "Unable" : "Pending",
        r.completed_at ? fmt(r.completed_at) : "—",
        r.completed_by_name || "—",
        r.notes || "",
      ];
    }),
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: [16, 23, 38], textColor: [212, 175, 55] },
    columnStyles: { 1: { cellWidth: 180 }, 6: { cellWidth: 100 } },
    margin: { left: marginX, right: marginX },
  });

  let cy = doc.lastAutoTable.finalY + 20;

  if (report.pos_reconciliation) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(20, 20, 20);
    doc.text("POS Reconciliation", marginX, cy);
    cy += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const p = report.pos_reconciliation;
    const rows = [
      ["Tills closed", p.tills_closed],
      ["Expected cash", `$${(p.expected_cash || 0).toFixed(2)}`],
      ["Actual cash", `$${(p.actual_cash || 0).toFixed(2)}`],
      ["Difference", `$${(p.difference || 0).toFixed(2)}`],
      ["Explanation", p.explanation || "—"],
      ["Verified by", p.verified_by || "—"],
    ];
    rows.forEach(([k, v]) => { doc.text(`${k}: ${v}`, marginX, cy); cy += 12; });
    cy += 6;
  }

  if (report.manager_comments) {
    doc.setFont("helvetica", "bold");
    doc.text("Manager Comments", marginX, cy);
    cy += 12;
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(report.manager_comments, 500);
    doc.text(lines, marginX, cy);
    cy += lines.length * 12 + 8;
  }

  const declaration = report.type === "opening"
    ? "I confirm that I have completed the store-opening inspection and that the store is safe, operational, clean, and ready for customers, except for any issues documented in this report."
    : "I confirm that I have completed the store-closing inspection, secured all cash and required areas, shut down the listed systems, armed the security alarm, and confirmed that the store is fully secured, except for any issues documented in this report.";
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  const dlines = doc.splitTextToSize(declaration, 500);
  doc.text(dlines, marginX, cy);
  cy += dlines.length * 12 + 10;

  if (report.signature) {
    try {
      doc.addImage(report.signature, "PNG", marginX, cy, 180, 60);
    } catch (err) {
      console.warn("Could not embed signature image in PDF", err);
    }
    cy += 62;
  }
  doc.setFont("helvetica", "normal");
  doc.text(`${report.signature_name || ""}   (ID: ${report.signature_employee_id || ""})`, marginX, cy);
  cy += 12;
  doc.text(`Signed: ${fmt(report.signature_timestamp || report.completed_time)}`, marginX, cy);

  doc.save(`${report.report_number || "report"}.pdf`);
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
